import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import navbar from '@/assets/data/navbar'
import stainingStudio from '@/assets/images/DigitalTry-in.svg'
import getRecipe from '@/assets/images/GetRecipe.svg'
import diamonIcon from '@/assets/images/Icons-diamond.png'
import digitalTryIn from '@/assets/images/StainingStudio.svg'
import wizards from '@/assets/images/wizards.svg'
import Chip from '@/components/Chip'
import Icon from '@/components/Icon'
import { useAppSelector } from '@/hooks/redux'
import { clearImageSelection, setImageId } from '@/store/project'
import { Features, Workflows } from '@/utils/constants'

import { imageType } from '../getRecipe/allInOne/utils/constants'

interface StartTaskModalProps {
  open: boolean
  onClose: () => void
  imageId: string
  caseId: string
  imageTypes?: string
}

interface NavbarItem {
  id: string
  label: string
  route?: string
  activeStep?: number
  children?: NavbarItem[]
  key: string
  badge?: string
}

const StartTaskModal: React.FC<StartTaskModalProps> = ({
  open,
  onClose,
  imageId,
  caseId,
  imageTypes,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'Navbar.nestedMenu',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'Navbar.navRecipes',
  })

  const { t: taskModal } = useTranslation('translation', {
    keyPrefix: 'StartTaskModal',
  })

  const [mainExpanded, setMainExpanded] = useState(true)
  const [wizardExpanded, setWizardExpanded] = useState(false)

  const [nestedExpanded, setNestedExpanded] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<NavbarItem | null>(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const handleNestedAccordion =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setNestedExpanded(isExpanded ? panel : null)
    }

  const handleItemClick = (item: NavbarItem) => {
    setSelectedItem(item)
  }

  const checkWorkflowAccess = (parentLabel: string, key: string) => {
    const mappedKey =
      key === 'WASHBAKE'
        ? 'FRAMEWORK_STAINING'
        : key === 'STAIN_&_GLAZE'
          ? 'STAINING_STUDIO'
          : key

    if (['Staining Studio', 'Matisse Lite'].includes(parentLabel)) {
      return isAccessible(mappedKey || '')
    }
    return isAccessibleWorkflow(parentLabel)
  }

  const handleStart = () => {
    if (!selectedItem || !caseId) return
    const parentItem = navbar.wizards.find((group) =>
      group.children?.some((child) => child.id === selectedItem.id),
    )
    const keyToCheck = parentItem?.key || selectedItem.key
    const isWorkflow = parentItem?.children?.length > 0

    const hasAccess = isWorkflow
      ? checkWorkflowAccess(keyToCheck, selectedItem?.key)
      : isAccessible(
          keyToCheck === 'STAIN_&_GLAZE' ? 'STAINING_STUDIO' : keyToCheck || '',
        )
    if (!hasAccess) {
      navigate('/plans')
      onClose()
      return
    }
    const separator = selectedItem?.route.includes('?') ? '&' : '?'
    navigate(`${selectedItem.route}${separator}caseId=${caseId}`)
    dispatch(setImageId(imageId))
    dispatch(clearImageSelection(caseId))
    onClose()
  }

  const renderListItem = (
    item: NavbarItem,
    isChild = false,
    showDiamond = false,
  ) => {
    const translate = item.key
    return (
      <ListItemButton
        key={item.id}
        dense
        onClick={() => handleItemClick(item)}
        selected={selectedItem?.id === item.id}
        sx={{
          px: isChild ? 6 : 4,
          py: '6px',
          my: '1px',
          bgcolor: isChild ? '#1b1b1b' : '#212121',
          color: 'white',
          '&.Mui-selected': {
            bgcolor: '#33393E',
          },
        }}
      >
        <ListItemText
          primary={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {isChild
                ? `${item.activeStep}. ${t(`${translate}`)}`
                : t(`${translate}`)}
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  variant="filled"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
              {showDiamond && (
                <img
                  src={diamonIcon}
                  alt="diamond icon"
                  style={{ height: '16px', marginLeft: '8px' }}
                />
              )}
            </span>
          }
          primaryTypographyProps={{
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            color: 'white',
          }}
        />
      </ListItemButton>
    )
  }

  const containsPhoto = (types?: string | string[]) => {
    if (!types) return false
    if (Array.isArray(types)) {
      return types.includes(imageType.PHOTO)
    }
    return types === imageType.PHOTO
  }

  const activePlan = useAppSelector((state) => state.app.activePlan)
  const subscribedFeatures = activePlan?.features?.map(
    (x: { feature: { key: any } }) => x?.feature?.key,
  )

  const isAccessible = (key: string) =>
    subscribedFeatures?.includes(Features[key as keyof typeof Features])

  const subscribedWorkflows = activePlan?.workflows?.map(
    (x: { workflow: { key: any } }) => x?.workflow?.key,
  )
  const isAccessibleWorkflow = (key: string) =>
    subscribedWorkflows?.includes(Workflows[key as keyof typeof Workflows])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e1e1e',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.25rem',
          backgroundColor: '#292929',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '16px 24px',
        }}
      >
        {taskModal('startTask')}
        <Icon icon={LuX} size={30} onClick={onClose} />
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: '#292929',
          color: 'white',
          p: 3,
          '&.MuiDialogContent-root': { pt: 3 },
        }}
      >
        <Typography
          fontSize={'14px'}
          fontWeight={400}
          lineHeight={'20px'}
          color="#C6C6C8"
          pb={2}
        >
          {taskModal('subtitle')}
        </Typography>

        {/* Get Recipe */}
        <Accordion
          expanded={mainExpanded}
          onChange={() => setMainExpanded(!mainExpanded)}
          sx={{
            borderRadius: 2,
            backgroundColor: 'black',
            color: 'white',
            boxShadow: 'none',
            '&:hover': { boxShadow: 3 },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{
              backgroundColor: '#323233',
              px: 2,
              py: 1,
              minHeight: 'unset',
              '&.Mui-expanded': {
                minHeight: 'unset',
              },
              '& .MuiAccordionSummary-content': {
                margin: 0,
                '&.Mui-expanded': {
                  margin: 0,
                },
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'white',
              }}
            >
              <img src={getRecipe} alt="" />
              <Box display={'flex'} gap={2}>
                <Typography>{taskModal('getRecipe')}</Typography>
                <Typography
                  fontSize={'16px'}
                  color="#C6C6C8"
                  lineHeight={'24px'}
                  fontWeight={400}
                >
                  {containsPhoto(imageTypes)
                    ? `1 ${taskModal('tasks')}`
                    : `3 ${taskModal('tasks')}`}
                </Typography>
              </Box>
            </Typography>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              backgroundColor: 'black',
              color: 'white',
              p: 0,
            }}
          >
            <List disablePadding>
              {navbar.getRecipe.map((item: NavbarItem) => {
                if (item.children?.length && containsPhoto(imageTypes)) {
                  return null
                }
                if (
                  !item.children?.length &&
                  item.label === 'ShadeGuide AI' &&
                  containsPhoto(imageTypes)
                ) {
                  return renderListItem(item)
                }
                if (
                  !item.children?.length &&
                  containsPhoto(imageTypes) &&
                  item.label !== 'ShadeGuide AI'
                ) {
                  return null
                }
                if (item.children?.length) {
                  return (
                    <Accordion
                      key={item.id}
                      expanded={nestedExpanded === item.id}
                      onChange={handleNestedAccordion(item.id)}
                      sx={{
                        boxShadow: 'none',
                        backgroundColor: '#212121',
                        color: 'white',
                        m: 0,
                        '&:hover': { boxShadow: 1 },
                        '&.Mui-expanded': {
                          my: '1px',
                        },
                        '&::before': {
                          display: 'none',
                        },
                        // borderBottom: '1px solid black',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                        sx={{
                          backgroundColor: '#212121',
                          px: 4,
                          py: '6px',
                          '& .MuiAccordionSummary-content': {
                            margin: 0,
                          },
                          minHeight: 'unset',
                          '&.Mui-expanded': {
                            minHeight: 'unset',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 400,
                            color: 'white',
                            fontSize: '16px',
                            lineHeight: '24px',
                          }}
                        >
                          {translate(`${item.key}`)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: 'black', p: 0 }}>
                        <List disablePadding>
                          {item.children.map((child) =>
                            renderListItem(
                              child,
                              true,
                              !checkWorkflowAccess(item.label, child.key),
                            ),
                          )}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                } else {
                  return renderListItem(item, false, !isAccessible(item.key))
                }
              })}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Wizards */}
        {!containsPhoto(imageTypes) && (
          <Accordion
            expanded={wizardExpanded}
            onChange={() => setWizardExpanded(!wizardExpanded)}
            sx={{
              borderRadius: 2,
              backgroundColor: 'black',
              color: 'white',
              boxShadow: 'none',
              '&:hover': { boxShadow: 3 },
              '&.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
              sx={{
                backgroundColor: '#323233',
                px: 2,
                py: 1,
                minHeight: 'unset',
                '&.Mui-expanded': {
                  minHeight: 'unset',
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'white',
                }}
              >
                <img src={wizards} alt="" />
                <Box display={'flex'} gap={2}>
                  <Typography>{taskModal('wizards')}</Typography>
                  <Typography
                    fontSize={'16px'}
                    color="#C6C6C8"
                    lineHeight={'24px'}
                    fontWeight={400}
                  >
                    6 {taskModal('tasks')}
                  </Typography>
                </Box>
              </Typography>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                backgroundColor: 'black',
                color: 'white',
                p: 0,
              }}
            >
              <List disablePadding>
                {navbar.wizards.map((item: NavbarItem) => {
                  if (item.children?.length && containsPhoto(imageTypes)) {
                    return null
                  }

                  if (item.children?.length) {
                    return (
                      <Accordion
                        key={item.id}
                        expanded={nestedExpanded === item.id}
                        onChange={handleNestedAccordion(item.id)}
                        sx={{
                          boxShadow: 'none',
                          backgroundColor: '#212121',
                          color: 'white',
                          m: 0,
                          '&:hover': { boxShadow: 1 },
                          '&.Mui-expanded': {
                            my: '1px',
                          },
                          '&::before': {
                            display: 'none',
                          },
                          borderBottom: '1px solid black',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon sx={{ color: 'white' }} />
                          }
                          sx={{
                            backgroundColor: '#212121',
                            px: 4,
                            py: '6px',
                            '& .MuiAccordionSummary-content': {
                              margin: 0,
                            },
                            minHeight: 'unset',
                            '&.Mui-expanded': {
                              minHeight: 'unset',
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 400,
                              color: 'white',
                              fontSize: '16px',
                              lineHeight: '24px',
                            }}
                          >
                            {translate(`${item.key}`)}
                            {item.badge && (
                              <Chip
                                label={item.badge}
                                size="small"
                                variant="filled"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                            {!isAccessibleWorkflow(item?.key) && (
                              <img
                                src={diamonIcon}
                                alt="diamond icon"
                                style={{
                                  height: '16px',
                                  marginLeft: '8px',
                                }}
                              />
                            )}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{ backgroundColor: 'black', p: 0 }}
                        >
                          <List disablePadding>
                            {item.children.map((child) =>
                              renderListItem(
                                child,
                                true,
                                !isAccessibleWorkflow(item.key),
                              ),
                            )}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    )
                  }
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {!containsPhoto(imageTypes) && (
          <Accordion
            sx={{
              borderRadius: 2,
              backgroundColor: 'black',
              color: 'white',
              boxShadow: 'none',
              '&:hover': { boxShadow: 3 },
              '&.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
              sx={{
                backgroundColor: '#323233',
                px: 2,
                py: 1,
                minHeight: 'unset',
                '&.Mui-expanded': {
                  minHeight: 'unset',
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'white',
                }}
              >
                <img src={stainingStudio} alt="" />
                <Box display={'flex'} gap={2}>
                  <Typography>{taskModal('stainingStudio')}</Typography>
                  <Typography
                    fontSize={'16px'}
                    color="#C6C6C8"
                    lineHeight={'24px'}
                    fontWeight={400}
                  >
                    4 {taskModal('tasks')}
                  </Typography>
                </Box>
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ backgroundColor: 'black', p: 0 }}>
              <List disablePadding>
                {navbar.stainingStudioRecipe.map((item: NavbarItem) =>
                  renderListItem(
                    item,
                    false,
                    !isAccessible(
                      item.key === 'WASHBAKE'
                        ? 'FRAMEWORK_STAINING'
                        : item.key || '',
                    ),
                  ),
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Digital Try-In */}
        <Accordion
          sx={{
            borderRadius: 2,
            backgroundColor: 'black',
            color: 'white',
            boxShadow: 'none',
            '&:hover': { boxShadow: 3 },
            '&.Mui-expanded': {
              mt: '1px',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{
              backgroundColor: '#323233',
              px: 2,
              py: 1,
              minHeight: 'unset',
              '&.Mui-expanded': {
                minHeight: 'unset',
              },
              '& .MuiAccordionSummary-content': {
                margin: 0,
                '&.Mui-expanded': {
                  margin: 0,
                },
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'white',
              }}
            >
              <img src={digitalTryIn} alt="" />
              <Box display={'flex'} gap={2}>
                <Typography>{taskModal('digitalTryIn')}</Typography>
                <Typography
                  fontSize={'16px'}
                  color="#C6C6C8"
                  lineHeight={'24px'}
                  fontWeight={400}
                >
                  2 {taskModal('tasks')}
                </Typography>
              </Box>
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ backgroundColor: 'black', p: 0 }}>
            <List disablePadding>
              {navbar.digitalData.map((item: NavbarItem) =>
                renderListItem(item),
              )}
            </List>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ p: '16px 24px', backgroundColor: '#292929' }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          sx={{
            borderRadius: '4px',
            borderColor: '#90CAf9',
            color: '#90CAf9',
          }}
        >
          {taskModal('cancelButton')}
        </Button>
        <Button
          onClick={handleStart}
          disabled={!selectedItem}
          variant="contained"
          sx={{
            borderRadius: '4px',
            backgroundColor: selectedItem
              ? '#90CAF9'
              : 'rgba(255, 255, 255, 0.12)',
            color: selectedItem ? 'black' : 'rgba(255, 255, 255, 0.30)',
          }}
        >
          {taskModal('startButton')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StartTaskModal
